import { isTextModel } from './modelKinds';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Page, type Schema } from '../api/client';
import { useStudio } from './App';
import { Field, Spinner, useApi } from './shared';
import { useTasks, type StudioTask } from './production';
import { mediaFailureMessage } from './mediaErrors';

export type SourceEvidence = {
  sourceCount: number;
  matchedSourceCount: number;
  selectedPassageCount: number;
  limited: boolean;
  excerpts: { sourceId: string; title: string; quote: string; offset: number }[];
};
export type CharacterImagePrompt = {
  version?: number;
  visualDescription?: string;
  selection?: string;
  prompt: string;
  facts: { category: string; detail: string; sourceId: string; quote: string }[];
  additions: string[];
  unknowns: string[];
};
export type ImageContext = {
  name: string;
  visualStyle?: string;
  textModelId?: string;
  approvedCharacter: { appearance?: string; description?: string };
  profile: Record<string, unknown>;
  sourceEvidence: SourceEvidence;
};

export function CharacterPromptBuilder({
  characterId,
  draft,
  context,
  taskPath,
  disabled,
  onAdopt,
}: {
  characterId: string;
  draft: Record<string, unknown>;
  context: ImageContext;
  taskPath: string;
  disabled: boolean;
  onAdopt: (prompt: string) => void;
}) {
  const { base, project, branch } = useStudio();
  const cache = useQueryClient();
  const models = useApi<Page<Schema<'ModelSummary'>>>(`${base}/models?role=character_designer`);
  const policy = useApi<Schema<'ModelPolicy'>>(`${base}/series/${project?.id}/model-policy`);
  const tasks = useTasks(taskPath);
  const [modelId, setModelId] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const options = models.data?.items.filter((m) => m.selectable && isTextModel(m)) ?? [];
  const selectedModel =
    modelId ||
    policy.data?.bindings.character_designer?.primary.catalogModelId ||
    context.textModelId ||
    options[0]?.catalogModelId ||
    '';
  const matching =
    tasks.data?.items.filter(
      (t) => t.artifactKind === 'character_image_prompt' && t.characterId === characterId,
    ) ?? [];
  const active = matching.find((t) => ['queued', 'running'].includes(t.state));
  const chosen = matching.find((t) => t.id === selectedTask) ?? active ?? matching[0];
  const candidate = chosen?.state === 'completed' ? chosen.result.characterImagePrompt : undefined;
  const evidence = candidate
    ? (chosen?.result.sourceEvidence ?? context.sourceEvidence)
    : context.sourceEvidence;
  async function extract() {
    if (!project || !branch) return;
    setBusy(true);
    setError('');
    try {
      const task = await api<StudioTask>(`${base}/studio-tasks`, 'POST', {
        kind: 'design',
        artifactKind: 'character_image_prompt',
        characterId,
        seriesId: project.id,
        branchId: branch.id,
        modelId: selectedModel,
        confirmed: true,
        currentDraft: draft,
        prompt:
          feedback.trim() ||
          '从原文提取此角色的一套静态基准造型，生成正面、左侧面、背面、右侧面四视图提示词；统一服装、年龄和发型，其他阶段单独列为待确认信息。',
      });
      setSelectedTask(task.id);
      await cache.invalidateQueries({ queryKey: ['api', taskPath] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="character-prompt-builder" aria-label="从原文提取角色造型">
      <strong>1. 从原文完善角色造型</strong>
      <p className="quiet">
        提取一套静态造型，用于同一角色的正面、左侧面、背面、右侧面四视图。四格统一服装与外观；人物经历、剧情动作和其他阶段不会拼进图片提示词。候选保存后由你审阅。
      </p>
      <details>
        <summary>
          {evidence.sourceCount
            ? `原文 ${evidence.sourceCount} 段 · 按角色姓名找到 ${evidence.matchedSourceCount} 段 · 选取 ${evidence.selectedPassageCount} 处`
            : '此角色尚未关联小说原文，将依据当前人设整理'}
          {evidence.limited ? '（已选取部分相关片段）' : ''}
        </summary>
        {evidence.excerpts.map((e, i) => (
          <blockquote key={`${e.sourceId}-${i}`}>
            <small>
              {e.sourceId} · {e.title}
            </small>
            <p>{e.quote}</p>
          </blockquote>
        ))}
        {!!evidence.sourceCount && !evidence.selectedPassageCount && (
          <p>
            没有找到含当前角色姓名的原文。若角色改过名或主要使用别名，请先核对角色姓名，也可在下方提供造型信息；手动补充的文字不会标成已核实的原文事实。
          </p>
        )}
      </details>
      <Field label="提取用的文字模型">
        <select
          value={selectedModel}
          disabled={busy || !!active}
          onChange={(e) => setModelId(e.target.value)}
        >
          {!options.some((m) => m.catalogModelId === selectedModel) && (
            <option value={selectedModel}>请选择可用文字模型</option>
          )}
          {options.map((m) => (
            <option key={m.catalogModelId} value={m.catalogModelId}>
              {m.displayName}
            </option>
          ))}
        </select>
      </Field>
      <Field label="造型要求或修改意见（选填）">
        <textarea
          rows={2}
          maxLength={4000}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="例如：以初次出场的造型为准，保留原文中的发簪和服装颜色。"
          disabled={busy || !!active}
        />
      </Field>
      <button
        type="button"
        className="button secondary"
        disabled={
          disabled ||
          busy ||
          !!active ||
          !options.some((m) => m.catalogModelId === selectedModel) ||
          tasks.isPending ||
          !!tasks.error ||
          policy.isPending ||
          !!policy.error
        }
        onClick={() => void extract()}
      >
        {(busy || active) && <Spinner />}
        {active
          ? '正在提取，可关闭后回来查看'
          : matching.length
            ? '重新提取并生成候选提示词'
            : '提取原文并生成提示词'}
      </button>
      {(error || models.error || policy.error || tasks.error) && (
        <p role="alert" className="form-error">
          {error || (models.error ?? policy.error ?? tasks.error)?.message}
        </p>
      )}
      {matching.length > 0 && (
        <Field label="已保存的提示词候选">
          <select value={chosen?.id ?? ''} onChange={(e) => setSelectedTask(e.target.value)}>
            {matching.map((t) => (
              <option key={t.id} value={t.id}>
                {new Date(t.createdAt).toLocaleString()} ·{' '}
                {t.state === 'completed'
                  ? '可审阅'
                  : t.state === 'running'
                    ? '提取中'
                    : t.state === 'queued'
                      ? '排队中'
                      : '未完成'}
              </option>
            ))}
          </select>
        </Field>
      )}
      {chosen?.errorMessage && (
        <p className="form-error" role="alert">
          {mediaFailureMessage(chosen)}
        </p>
      )}
      {candidate && (
        <div className="character-prompt-candidate">
          {candidate.version !== 2 && (
            <p role="status" className="quiet">
              这是旧版候选，可能混有剧情或多个阶段。请点击“重新提取并生成候选提示词”，整理成一套四视图造型。
            </p>
          )}
          {candidate.selection && (
            <p>
              <strong>本套造型：</strong>
              {candidate.selection}
            </p>
          )}
          <details open>
            <summary>原文依据 · {candidate.facts.length} 项</summary>
            {!candidate.facts.length && <p>本候选没有可核对的原文依据，请检查下方补充设计。</p>}
            {candidate.facts.map((f, i) => (
              <blockquote key={i}>
                <strong>
                  {f.category}：{f.detail}
                </strong>
                <p>“{f.quote}”</p>
                <small>出处：{f.sourceId}</small>
              </blockquote>
            ))}
          </details>
          {!!candidate.additions.length && (
            <details open>
              <summary>补充设计 · 原文未明确</summary>
              <ul>
                {candidate.additions.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            </details>
          )}
          {!!candidate.unknowns.length && (
            <details>
              <summary>待确认的信息</summary>
              <ul>
                {candidate.unknowns.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            </details>
          )}
          <Field label="完整提示词候选">
            <textarea readOnly rows={8} value={candidate.prompt} />
          </Field>
          <button
            type="button"
            className="button secondary"
            disabled={disabled || candidate.version !== 2 || !candidate.visualDescription}
            onClick={() => onAdopt(candidate.visualDescription ?? '')}
          >
            使用这版提示词，继续编辑
          </button>
        </div>
      )}
    </section>
  );
}
